/**
 * DMC 鬼泣式华丽度评级与战斗增幅引擎 (DMC Stylish Rank & Combat Multiplier)
 * 职责：
 * 1. 毫秒级动态槽衰减与梯度抗性演进
 * 2. 评级晋升与连击加成
 * 3. 战斗增幅：高评级 (S / SS / SSS) 触发爆发模式，大幅倍增进攻烈度
 */

import type { DmcGrade } from "../../shared/protocol";
import { SoundEngine } from "./soundEngine";

export interface DmcTierConfig {
  id: DmcGrade;
  title: string;
  decayRate: number; // % / 秒
  resistance: number;
  attackMultiplier: number; // 攻击增幅倍率
  dodgeChance: number; // 受击时触发 MISS 闪避的概率 (0 ~ 0.55)
}

export const DMC_TIERS: DmcTierConfig[] = [
  { id: "D", title: "DOPE", decayRate: 14, resistance: 1.0, attackMultiplier: 1.0, dodgeChance: 0 },
  { id: "C", title: "COOL", decayRate: 18, resistance: 1.2, attackMultiplier: 1.1, dodgeChance: 0.05 },
  { id: "B", title: "BADASS", decayRate: 22, resistance: 1.5, attackMultiplier: 1.25, dodgeChance: 0.15 },
  { id: "A", title: "APOCALYPTIC", decayRate: 26, resistance: 1.9, attackMultiplier: 1.5, dodgeChance: 0.25 },
  { id: "S", title: "SAVAGE", decayRate: 30, resistance: 2.4, attackMultiplier: 1.8, dodgeChance: 0.35 },
  { id: "SS", title: "SICK SKILLS", decayRate: 35, resistance: 3.0, attackMultiplier: 2.2, dodgeChance: 0.45 },
  { id: "SSS", title: "SMOKIN' SEXY STYLE!", decayRate: 40, resistance: 3.8, attackMultiplier: 3.0, dodgeChance: 0.55 },
];

export function getDodgeChanceByGrade(grade: DmcGrade): number {
  const found = DMC_TIERS.find((t) => t.id === grade);
  return found ? found.dodgeChance : 0;
}

export class DmcRankManager {
  private currentTierIdx = 0;
  private currentGauge = 0; // 0 - 100
  private lastActionTime = 0;
  private graceUntil = 0;
  private animFrameId: number | null = null;
  private sounds: SoundEngine | null = null;
  private onChangeCallbacks: ((grade: DmcGrade, gauge: number, multiplier: number) => void)[] = [];

  constructor() {
    this.reset();
  }

  public bindSounds(sounds: SoundEngine) {
    this.sounds = sounds;
  }

  public onRankChange(cb: (grade: DmcGrade, gauge: number, multiplier: number) => void) {
    this.onChangeCallbacks.push(cb);
  }

  public get grade(): DmcGrade {
    return DMC_TIERS[this.currentTierIdx].id;
  }

  public get gauge(): number {
    return this.currentGauge;
  }

  public get attackMultiplier(): number {
    return DMC_TIERS[this.currentTierIdx].attackMultiplier;
  }

  public reset() {
    this.currentTierIdx = 0;
    this.currentGauge = 0;
    this.lastActionTime = Date.now();
    this.graceUntil = 0;
    this.notify();
  }

  public start() {
    if (this.animFrameId !== null) return;
    let lastTime = performance.now();

    const loop = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      this.tick(dt);
      this.animFrameId = requestAnimationFrame(loop);
    };

    this.animFrameId = requestAnimationFrame(loop);
  }

  public stop() {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
  }

  private notify() {
    const grade = this.grade;
    const gauge = this.currentGauge;
    const mult = this.attackMultiplier;
    for (const cb of this.onChangeCallbacks) {
      cb(grade, gauge, mult);
    }
  }

  private tick(dt: number) {
    const now = Date.now();
    if (now < this.graceUntil) return;

    const tier = DMC_TIERS[this.currentTierIdx];
    const drop = tier.decayRate * dt;

    if (this.currentGauge > drop) {
      this.currentGauge -= drop;
    } else {
      // 降级
      if (this.currentTierIdx > 0) {
        this.currentTierIdx--;
        this.currentGauge = 80;
      } else {
        this.currentGauge = 0;
      }
    }
    this.notify();
  }

  /**
   * 方块合并增加华丽度点数
   */
  public addMergeScore(val: number) {
    const tier = DMC_TIERS[this.currentTierIdx];
    const now = Date.now();

    // 冻结保护时长 (ms)
    this.graceUntil = now + Math.min(1800, 800 + Math.log2(val) * 100);

    // 计算增加的能量
    const baseGain = Math.log2(val) * 12;
    const netGain = baseGain / tier.resistance;

    this.currentGauge += netGain;

    if (this.currentGauge >= 100) {
      if (this.currentTierIdx < DMC_TIERS.length - 1) {
        // 晋级
        this.currentTierIdx++;
        this.currentGauge = 35; // 晋升保底缓冲
        if (this.sounds) {
          this.sounds.playClutch();
        }
      } else {
        this.currentGauge = 100;
      }
    }
    this.notify();
  }
}

